import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Loader2, Phone, ShieldCheck, Home, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AuthModal() {
  const { modalOpen, closeLogin, completeLogin, pendingRedirect, pendingState } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("role");
  const [role, setRole] = useState("homeowner");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const otpRef = useRef(null);

  const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailOk = emailRx.test(email.trim());

  useEffect(() => {
    if (!modalOpen) return;
    setStep("role");
    setRole("homeowner");
    setName("");
    setEmail("");
    setPhone("");
    setOtp("");
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLogin();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeLogin]);

  const formatPhone = (raw) => raw.replace(/[^\d]/g, "").slice(0, 10);

  // === Direct REST SendOTP flow — no captcha, OTP arrives via SMS ===
  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("Enter your name");
    if (!emailOk) return toast.error("Enter a valid email (e.g. you@example.com)");
    if (phone.length !== 10) return toast.error("Enter a valid 10-digit Indian mobile");
    try {
      setLoading(true);
      const { data } = await api.post("/auth/send-otp", { phone: `+91${phone}` });
      setStep("otp");
      setTimeout(() => otpRef.current?.focus(), 100);
      if (data.provider === "msg91") {
        toast.success("OTP sent to your phone via SMS");
      } else {
        toast.success("OTP sent (dev mode)");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 4) return toast.error("Enter the 4-digit OTP");
    try {
      setLoading(true);
      const { data } = await api.post("/auth/verify-otp", {
        phone: `+91${phone}`,
        otp,
        role,
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      const token = data.token || data.access_token;
      if (!token) throw new Error("No token in response");
      completeLogin(token, data.user || null);
      localStorage.setItem("zumroo_last_active", String(Date.now()));
      toast.success("Welcome to Zumroo!");
      navigate(pendingRedirect || "/listings", {
        replace: true,
        state: pendingState || undefined,
      });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = () => sendOtp();

  return (
    <AnimatePresence>
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(26,35,30,0.65)] backdrop-blur-sm"
          onClick={closeLogin}
          data-testid="auth-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl w-full max-w-md p-6 sm:p-8 shadow-2xl"
          >
            <button
              onClick={closeLogin}
              className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-[#F7F6F2] text-[#5C6B62] flex items-center justify-center"
              data-testid="auth-modal-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {step === "role" && (
              <>
                <div className="label-mono mb-2">Get started</div>
                <h2 className="text-2xl font-bold tracking-tight text-[#1A231E]">
                  Choose how you'll use Zumroo
                </h2>
                <p className="text-sm text-[#5C6B62] mt-1">Sign in to browse listings.</p>

                <div className="mt-5 space-y-3">
                  <button
                    type="button"
                    onClick={() => setRole("homeowner")}
                    className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition text-left ${
                      role === "homeowner"
                        ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                        : "bg-white border-[#E4E1D9] text-[#1A231E] hover:border-[#1E3F33]"
                    }`}
                    data-testid="modal-role-homeowner"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      role === "homeowner" ? "bg-white/15 text-white" : "bg-[#EAF1ED] text-[#1E3F33]"
                    }`}>
                      <Home className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">I'm a Home Owner</div>
                      <div className={`text-xs ${role === "homeowner" ? "text-white/70" : "text-[#5C6B62]"}`}>
                        List for free, get verified buyers
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole("broker")}
                    className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition text-left ${
                      role === "broker"
                        ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                        : "bg-white border-[#E4E1D9] text-[#1A231E] hover:border-[#1E3F33]"
                    }`}
                    data-testid="modal-role-broker"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      role === "broker" ? "bg-white/15 text-white" : "bg-[#FBEFE9] text-[#C86646]"
                    }`}>
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">I'm a Broker</div>
                      <div className={`text-xs ${role === "broker" ? "text-white/70" : "text-[#5C6B62]"}`}>
                        Unlock owner contacts for ₹49 / ₹79
                      </div>
                    </div>
                  </button>
                </div>

                <button
                  onClick={() => setStep("phone")}
                  className="btn-primary w-full justify-center mt-5"
                  data-testid="modal-role-continue-btn"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}

            {step === "phone" && (
              <form onSubmit={sendOtp}>
                <button
                  type="button"
                  onClick={() => setStep("role")}
                  className="text-sm text-[#5C6B62] hover:text-[#1A231E] flex items-center gap-1 mb-3"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Change role
                </button>
                <div className="label-mono mb-1">Step 1 of 2</div>
                <h2 className="text-2xl font-bold tracking-tight text-[#1A231E]">
                  What's your phone?
                </h2>
                <p className="text-sm text-[#5C6B62] mt-1">
                  We'll send a 4-digit OTP to verify it.
                </p>

                <div className="mt-5 space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">
                      Your name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Full name"
                      className="w-full px-4 py-3 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none bg-white"
                      data-testid="modal-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={`w-full px-4 py-3 rounded-xl border bg-white outline-none focus:ring-2 ${
                        email && !emailOk
                          ? "border-[#B23A48] focus:ring-red-100"
                          : "border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-[#EAF1ED]"
                      }`}
                      data-testid="modal-email-input"
                      required
                    />
                    {email && !emailOk && (
                      <div className="text-xs text-[#B23A48] mt-1">Enter a valid email (e.g. name@domain.com)</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">
                      Mobile number
                    </label>
                    <div className="flex items-center rounded-xl border border-[#E4E1D9] bg-white overflow-hidden focus-within:border-[#1E3F33] focus-within:ring-2 focus-within:ring-[#EAF1ED]">
                      <div className="px-3 py-3 bg-[#F7F6F2] border-r border-[#E4E1D9] font-semibold text-[#1A231E] text-sm">
                        🇮🇳 +91
                      </div>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        placeholder="9876543210"
                        className="flex-1 px-4 py-3 outline-none text-[#1A231E]"
                        data-testid="modal-phone-input"
                        autoFocus
                        required
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || phone.length !== 10 || !name.trim() || !emailOk}
                  className="btn-primary w-full justify-center mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="modal-send-otp-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                  Send OTP
                </button>
              </form>
            )}

            {step === "otp" && (
              <form onSubmit={verifyOtp}>
                <button
                  type="button"
                  onClick={() => setStep("phone")}
                  className="text-sm text-[#5C6B62] hover:text-[#1A231E] flex items-center gap-1 mb-3"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Change number
                </button>
                <div className="label-mono mb-1">Step 2 of 2</div>
                <h2 className="text-2xl font-bold tracking-tight text-[#1A231E]">
                  Enter the OTP
                </h2>
                <p className="text-sm text-[#5C6B62] mt-1">
                  Sent via SMS to <span className="font-semibold text-[#1A231E]">+91 {phone}</span>
                </p>

                <input
                  ref={otpRef}
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="• • • •"
                  maxLength={4}
                  className="w-full px-4 py-4 mt-5 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none bg-white text-center text-2xl font-mono tracking-[0.4em] font-bold"
                  data-testid="modal-otp-input"
                  autoComplete="one-time-code"
                  required
                />

                <button
                  type="submit"
                  disabled={loading || otp.length !== 4}
                  className="btn-primary w-full justify-center mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="modal-verify-otp-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Verify &amp; continue
                </button>

                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={loading}
                  className="block mx-auto mt-3 text-xs text-[#5C6B62] hover:text-[#1A231E] disabled:opacity-50"
                  data-testid="modal-resend-otp-btn"
                >
                  Didn't get it? <span className="text-[#C86646] font-semibold">Resend</span>
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
